import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { settingsAPI } from '../api';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

const STATUS_CONFIG = {
  trial:             { label: 'TRIAL',             bg: '#f59e0b', color: '#fff' },
  recipe_confirmed:  { label: 'RECIPE',            bg: '#10b981', color: '#fff' },
  commercial_trial:  { label: 'COMM. TRIAL',       bg: '#3b82f6', color: '#fff' },
  launched:          { label: 'LIVE',              bg: '#16a34a', color: '#fff' },
};

export default function DepartmentCardReport() {
  const [depts, setDepts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    settingsAPI.getType('department').then(r => setDepts(r.data));
    API.get('/projects').then(r => setProjects(r.data));
  }, []);

  const generate = async () => {
    if (!selectedDept) return toast.error('Please select a department');
    setLoading(true);
    try {
      const apiParams = { department: selectedDept };
      if (selectedProject) apiParams.project_id = selectedProject;
      const r = await API.get('/products', { params: apiParams });
      let products = r.data;
      if (fromDate) products = products.filter(p => {
        const d = new Date(p.created_at); return d >= new Date(fromDate);
      });
      if (toDate) products = products.filter(p => {
        const d = new Date(p.created_at); const t = new Date(toDate); t.setHours(23,59,59); return d <= t;
      });
      setData(products);
      setGenerated(true);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const printPDF = () => {
    if (!data) return;
    const today = new Date().toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' });
    const dateRange = fromDate || toDate ? `${fromDate||'All'} — ${toDate||'Present'}` : 'All Dates';

    const cards = data.map(p => {
      const sc = STATUS_CONFIG[p.status] || { label: p.status?.toUpperCase(), bg: '#888', color: '#fff' };
      return `<div style="background:#f0f4ff;border-radius:12px;padding:18px 14px 14px;border:1px solid #d0d9f0;display:flex;flex-direction:column;justify-content:space-between;min-height:130px;">
        <div style="font-size:13px;font-weight:700;color:#1a2a5e;line-height:1.3;text-transform:uppercase;letter-spacing:0.02em;">${p.name}</div>
        <div style="margin-top:10px;">
          <span style="display:inline-block;padding:3px 10px;border-radius:6px;font-size:9px;font-weight:700;background:${sc.bg};color:${sc.color};letter-spacing:0.08em;">${sc.label}</span>
        </div>
        <div style="margin-top:8px;padding-top:8px;border-top:1px solid #c8d2e8;font-size:10px;color:#4a5a8a;font-weight:500;">${{'trial':'Trial','recipe_confirmed':'Recipe Confirmed','commercial_trial':'Commercial Trial','launched':'Launched'}[p.status]||p.status}</div>
      </div>`;
    }).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap" rel="stylesheet">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'Inter',Arial,sans-serif;background:#1a2a5e;padding:32px;color:#fff;min-height:100vh;}
      .header{margin-bottom:24px;}
      .dept-tag{display:inline-block;background:#2d3f7a;padding:6px 18px;border-radius:4px;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#a0b0e0;margin-bottom:14px;}
      .title{font-size:28px;font-weight:800;color:#fff;letter-spacing:-0.01em;}
      .title span{display:inline-block;width:60px;height:3px;background:#4a6ef5;margin-left:12px;vertical-align:middle;border-radius:2px;}
      .meta{font-size:10px;color:#6080b0;margin-top:6px;letter-spacing:0.06em;}
      .grid{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;}
      @media print{
        body{padding:20px;background:#1a2a5e !important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        @page{margin:0;size:A4 landscape;}
      }
    </style></head><body>
    <div class="header">
      <div class="dept-tag">${selectedDept}${selectedProject ? ' · ' + (projects.find(p => String(p.id) === String(selectedProject))?.name || '') : ''}</div>
      <div class="title">${selectedProject ? (projects.find(p => String(p.id) === String(selectedProject))?.name || selectedDept) : selectedDept}<span></span></div>
      <div class="meta">DATE RANGE: ${dateRange} &nbsp;·&nbsp; ${data.length} PRODUCTS &nbsp;·&nbsp; GENERATED: ${today}</div>
    </div>
    <div class="grid">${cards}</div>
    </body></html>`);
    win.document.close();
    win.focus();
  };

  const statusCount = data ? data.reduce((acc, p) => { acc[p.status]=(acc[p.status]||0)+1; return acc; }, {}) : {};

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Department Card Report</div>
          <div className="page-subtitle">Presentable product cards grouped by department</div>
        </div>
        {generated && data && (
          <button className="btn btn-primary" onClick={printPDF}>⬇ Download / Print PDF</button>
        )}
      </div>

      {/* Filter Panel */}
      <div className="card mb-6">
        <div className="card-title">Report Options</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
            <label>Department *</label>
            <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
              <option value="">Select department</option>
              {depts.map(d => <option key={d.id} value={d.value}>{d.value}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
            <label>Project (optional)</label>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
              <option value="">All Projects</option>
              {projects.filter(p => !selectedDept || p.department === selectedDept).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ width: 160 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ width: 160 }} />
          </div>
          <button className="btn btn-primary" onClick={generate} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {generated && (
            <button className="btn btn-secondary" onClick={() => { setGenerated(false); setData(null); }}>
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {generated && data && (
        <>
          {/* Status summary */}
          <div className="stats-grid mb-6">
            {['trial','recipe_confirmed','commercial_trial','launched'].map(s => (
              <div key={s} className="stat-card">
                <div className="stat-label">{STATUS_CONFIG[s]?.label}</div>
                <div className="stat-value">{statusCount[s]||0}</div>
              </div>
            ))}
          </div>

          {/* Card preview */}
          <div style={{
            background: '#1a2a5e',
            borderRadius: 12,
            padding: 28,
            marginBottom: 24,
          }}>
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'inline-block', background: '#2d3f7a', padding: '5px 16px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#a0b0e0', marginBottom: 10 }}>
                {selectedDept}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>
                  {selectedProject ? projects.find(p => String(p.id) === String(selectedProject))?.name || selectedDept : selectedDept}
                </div>
                <div style={{ width: 48, height: 3, background: '#4a6ef5', borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 10, color: '#6080b0', marginTop: 4, letterSpacing: '0.06em' }}>
                {data.length} PRODUCTS
                {(fromDate || toDate) && ` · ${fromDate||'All'} — ${toDate||'Present'}`}
              </div>
            </div>

            {data.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6080b0' }}>No products found for selected filters</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                {data.map(p => {
                  const sc = STATUS_CONFIG[p.status] || { label: p.status?.toUpperCase(), bg: '#888', color: '#fff' };
                  return (
                    <div key={p.id} style={{
                      background: '#f0f4ff',
                      borderRadius: 12,
                      padding: '16px 14px 12px',
                      border: '1px solid #d0d9f0',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: 120,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2a5e', lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                        {p.name}
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                          fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.color, letterSpacing: '0.08em'
                        }}>{sc.label}</span>
                      </div>
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #c8d2e8', fontSize: 10, color: '#4a5a8a', fontWeight: 500 }}>
                        {{'trial':'Trial','recipe_confirmed':'Recipe Confirmed','commercial_trial':'Commercial Trial','launched':'Launched'}[p.status]||p.status}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
